import * as React from 'react';
import { t, tryT, type MessageKey } from '@reos/i18n';
import { systemClock } from '@reos/config';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { EmptyState } from '@redhand/ui';
import { ListTable } from '../components/list-table.js';
import { PageHead } from '../components/page-head.js';
import { SupportWidget } from '../components/SupportWidget.js';
import {
  CELL_STATUS_LABELS,
  cellNextStep,
  connectionState,
  createPublication,
  ERROR_NEXT_STEPS,
  fetchConnections,
  fetchExportPolicy,
  fetchPortalAccess,
  fetchPreview,
  fetchPublication,
  fetchRegistry,
  fetchSyncCenter,
  fetchUnitedClassifiedsFeedStatus,
  PORTAL_STATUS_LABELS,
  queuePublication,
  reopenPublication,
  requestPortalOperation,
  rotateUnitedClassifiedsFeedToken,
  saveConnection,
  saveCredentials,
  saveExportPolicy,
  syncCenterDealStatus,
  UNITED_CLASSIFIEDS_PORTAL,
  unitedClassifiedsFeedUrl,
  validatePublication,
  type ConnectionDto,
  type PortalAccessDto,
  type PortalConnectionState,
  type PortalDescriptor,
  type PortalOperation,
  type SyncCenterCell,
  type SyncCenterView,
} from '../lib/connectors.js';
/*
 * Krok SEZ1 bod 1: souhrn „N z M zveřejněno" i vycet „zverejneno" ziji v
 * `lib/portal-export-summary.ts` — tentyz vypocet cte od SEZ1 i sloupec
 * „Portály" v seznamu nemovitosti.
 */
import { isPublishedStatus, portalExportSummary } from '../lib/portal-export-summary.js';
import { formatRelativeChange } from '../lib/relative-time.js';
// Krok PORT12: uvitaci kredit je Money ({amount, currency}) — formatuje se
// tim, co uz formatuje ceny nemovitosti, ne vlastnim Intl volanim.
import { formatMoney } from '../lib/properties.js';
import { listMembers } from '../lib/members-api.js';
import {
  getTenantSettings,
  setPortalLiveExport,
  setRequireOwnerApproval,
} from '../lib/assistant-settings-api.js';
import { listingDealStatusLabel } from '../components/listing-deal-block.js';

/**
 * Portal Sync Center (§36) + connection wizard (§32). VSECHNY capabilities,
 * limity, auth pole a stavy prichazi z /v1/connectors/registry - zadny
 * hardcode (§31). Zadne fake odesilani (§128): DEMO rezim ukazuje jen dry-run
 * nahled payloadu a poctive stavy PARTNER_REQUIRED / PENDING_SPEC.
 */

const badgeClass = (status: string): string => {
  if (isPublishedStatus(status)) return 'rh-badge rh-badge--success';
  if (status === 'ERROR') return 'rh-badge rh-badge--danger';
  if (status === 'QUEUED' || status === 'VALIDATED' || status === 'NOT_PUBLISHED')
    return 'rh-badge rh-badge--warning';
  return 'rh-badge rh-badge--neutral';
};

const connectionBadgeClass: Readonly<Record<ReturnType<typeof connectionState>, string>> = {
  connected: 'rh-badge rh-badge--success',
  error: 'rh-badge rh-badge--danger',
  preview: 'rh-badge rh-badge--warning',
  disconnected: 'rh-badge rh-badge--neutral',
};

const CATEGORY_LABELS: Readonly<Record<string, string>> = {
  APARTMENT: t('webAdmin.portaly.category.APARTMENT', { locale: 'cs' }),
  HOUSE: t('webAdmin.portaly.category.HOUSE', { locale: 'cs' }),
  LAND: t('webAdmin.portaly.category.LAND', { locale: 'cs' }),
  COMMERCIAL: t('webAdmin.portaly.category.COMMERCIAL', { locale: 'cs' }),
  OTHER: t('webAdmin.portaly.category.OTHER', { locale: 'cs' }),
};

function muted(text: string): React.JSX.Element {
  return (
    <span className="text-sm" style={{ color: 'var(--rh-text-muted)' }}>
      {text}
    </span>
  );
}

// ---- detail publikace -------------------------------------------------------

/**
 * Krok SR1: operace, ktere jsou nad publikaci v danem stavu mozne. Pravidlo
 * drzi server (`allowedOperations` v @reos/listing) — tady je jen zrcadlo,
 * aby UI nenabizelo tlacitko, ktere skonci 409.
 */
function allowedForState(state: string | null): readonly PortalOperation[] {
  if (state === 'published' || state === 'updated') return ['UPDATE', 'WITHDRAW', 'RENEW', 'TOP'];
  if (state === 'error') return ['WITHDRAW'];
  return [];
}

function PublicationPanel({
  cell,
  onChanged,
}: {
  readonly cell: SyncCenterCell;
  readonly onChanged: () => void;
}): React.JSX.Element {
  const [showPreview, setShowPreview] = React.useState(false);
  const [withPhotos, setWithPhotos] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const publicationQuery = useQuery({
    queryKey: ['publication', cell.publicationId],
    queryFn: () => fetchPublication(cell.publicationId ?? ''),
    enabled: cell.publicationId !== null,
  });
  const previewQuery = useQuery({
    queryKey: ['publication-preview', cell.publicationId],
    queryFn: () => fetchPreview(cell.publicationId ?? ''),
    enabled: cell.publicationId !== null && showPreview,
  });

  const act = (fn: () => Promise<unknown>) => {
    setError(null);
    void fn()
      .then(() => {
        void publicationQuery.refetch();
        onChanged();
      })
      .catch((err: unknown) => setError(err instanceof Error ? err.message : String(err)));
  };

  const publication = publicationQuery.data;
  const canCreate = cell.publicationId === null;
  const state = publication?.state ?? null;

  return (
    <div className="rh-card p-4 mt-4" data-testid="publication-panel">
      <h3 className="font-semibold mb-2">
        {t('webAdmin.portaly.publicationHeading', { locale: 'cs', values: { portal: cell.portal } })}{' '}
        <span className={badgeClass(cell.status)}>{CELL_STATUS_LABELS[cell.status] ?? cell.status}</span>
      </h3>
      {error !== null ? (
        <p className="text-sm mb-2" style={{ color: 'var(--rh-danger)' }} data-testid="publication-error">
          {error}
        </p>
      ) : null}
      <div className="flex flex-wrap gap-2 mb-3">
        {canCreate ? (
          <button
            type="button"
            className="px-4 py-1 bg-neutral-900 text-rh-on-accent rounded text-sm"
            onClick={() => act(() => createPublication(cell.listingId, cell.portal))}
          >
            {t('webAdmin.portaly.createDraftPublication', { locale: 'cs' })}
          </button>
        ) : null}
        {state === 'draft' ? (
          <button
            type="button"
            className="px-4 py-1 bg-neutral-900 text-rh-on-accent rounded text-sm"
            onClick={() => act(() => validatePublication(cell.publicationId ?? ''))}
          >
            {t('webAdmin.portaly.validateForPortal', { locale: 'cs' })}
          </button>
        ) : null}
        {cell.publicationId !== null ? (
          <button
            type="button"
            className="px-4 py-1 border rounded text-sm"
            onClick={() => setShowPreview((value) => !value)}
          >
            {showPreview
              ? t('webAdmin.portaly.hidePreview', { locale: 'cs' })
              : t('webAdmin.portaly.showPreview', { locale: 'cs' })}
          </button>
        ) : null}
        {state === 'validated' || state === 'error' ? (
          <button
            type="button"
            className="px-4 py-1 border rounded text-sm"
            onClick={() => act(() => queuePublication(cell.publicationId ?? ''))}
          >
            {state === 'error'
              ? t('webAdmin.portaly.retryQueue', { locale: 'cs' })
              : t('webAdmin.portaly.enqueueTest', { locale: 'cs' })}
          </button>
        ) : null}
      </div>

      {/* Krok SR1 bod 1: zbytek zivotniho cyklu. Nabizi se jen operace, ktere
          jsou v danem stavu podle serveru mozne (`ALLOWED_OPERATIONS`) —
          zadne tlacitko, ktere by skoncilo chybou. */}
      {allowedForState(state).length > 0 ? (
        <div className="mb-3" data-testid="publication-operations">
          <p className="text-sm font-medium">{t('webAdmin.portaly.sr1.lifecycleHeading', { locale: 'cs' })}</p>
          <label className="text-sm flex items-center gap-1 mt-1">
            <input
              type="checkbox"
              checked={withPhotos}
              onChange={(event) => setWithPhotos(event.target.checked)}
              data-testid="operation-photos"
            />
            {t('webAdmin.portaly.sr1.includePhotos', { locale: 'cs' })}
          </label>
          <div className="flex flex-wrap gap-2 mt-2">
            {allowedForState(state).map((operation) => (
              <button
                key={operation}
                type="button"
                className={
                  operation === 'TOP'
                    ? 'px-4 py-1 border rounded text-sm rh-badge--warning'
                    : 'px-4 py-1 border rounded text-sm'
                }
                title={t(`webAdmin.portaly.sr1.operationHint.${operation}`, { locale: 'cs' })}
                data-testid={`operation-${operation}`}
                onClick={() =>
                  act(() =>
                    requestPortalOperation(cell.publicationId ?? '', { operation, includePhotos: withPhotos }),
                  )
                }
              >
                {t(`webAdmin.portaly.sr1.operation.${operation}`, { locale: 'cs' })}
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {state === 'removed' ? (
        <div className="mb-3" data-testid="publication-reopen">
          <button
            type="button"
            className="px-4 py-1 border rounded text-sm"
            onClick={() => act(() => reopenPublication(cell.publicationId ?? ''))}
          >
            {t('webAdmin.portaly.sr1.reopen', { locale: 'cs' })}
          </button>{' '}
          {muted(t('webAdmin.portaly.sr1.reopenHint', { locale: 'cs' }))}
        </div>
      ) : null}

      {publication?.validation ? (
        <div className="mb-3" data-testid="validation-result">
          <p className="text-sm font-medium">
            {t('webAdmin.portaly.validationHeading', { locale: 'cs', values: { ruleVersion: publication.validation.ruleVersion } })}{' '}
            {publication.validation.ok
              ? t('webAdmin.portaly.validationOk', { locale: 'cs' })
              : t('webAdmin.portaly.blockersCount', { locale: 'cs', values: { count: String(publication.validation.blockers.length) } })}
            {publication.validation.warnings.length > 0
              ? t('webAdmin.portaly.warningsCountSuffix', { locale: 'cs', values: { count: String(publication.validation.warnings.length) } })
              : ''}
          </p>
          <ul className="text-sm list-disc pl-5">
            {[...publication.validation.blockers, ...publication.validation.warnings].map((issue) => (
              <li key={`${issue.severity}:${issue.rule}:${issue.field}`}>
                <span className={issue.severity === 'BLOCKER' ? 'rh-badge rh-badge--danger' : 'rh-badge rh-badge--warning'}>
                  {issue.severity === 'BLOCKER'
                    ? t('webAdmin.portaly.blocker', { locale: 'cs' })
                    : t('webAdmin.portaly.warning', { locale: 'cs' })}
                </span>{' '}
                <code>{issue.field}</code> — <code>{issue.rule}</code>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {publication?.lastError ? (
        <div className="mb-3" data-testid="last-error">
          <p className="text-sm font-medium" style={{ color: 'var(--rh-danger)' }}>
            {t('webAdmin.portaly.lastErrorPrefix', {
              locale: 'cs',
              values: { code: publication.lastError.code, portalStatus: String(publication.lastError.portalStatus) },
            })}
          </p>
          <p className="text-sm">{ERROR_NEXT_STEPS[publication.lastError.code] ?? ''}</p>
          <pre className="text-xs p-2 rounded overflow-auto max-h-32" style={{ background: 'var(--rh-canvas)' }}>
            {publication.lastError.raw}
          </pre>
        </div>
      ) : null}

      {showPreview && previewQuery.data ? (
        <div data-testid="dry-run-preview">
          <p className="text-sm font-medium">
            {t('webAdmin.portaly.dryRunLine', {
              locale: 'cs',
              values: { method: previewQuery.data.method, contentType: previewQuery.data.contentType },
            })}
          </p>
          {previewQuery.data.unmapped.length > 0 ? (
            <p className="text-sm">
              {t('webAdmin.portaly.unmappedFieldsPrefix', { locale: 'cs' })}{' '}
              {previewQuery.data.unmapped.map((u) => `${u.field} (${u.reason})`).join(', ')}
            </p>
          ) : null}
          <pre className="text-xs p-2 rounded overflow-auto max-h-64" style={{ background: 'var(--rh-canvas)' }}>
            {previewQuery.data.requestBody}
          </pre>
        </div>
      ) : null}
      {showPreview && previewQuery.isError ? muted(t('webAdmin.portaly.previewUnavailable', { locale: 'cs' })) : null}
    </div>
  );
}

// ---- connection wizard (§32) ------------------------------------------------

/**
 * Krok UXP7: makléř vidí jen jeden hlavní stav (Nepřipojeno / Připojeno —
 * inzeráty odcházejí / Chyba přihlášení) a jedno tlačítko. Technický stav
 * `preview` (`connectionState`) je z pohledu makléře taky „Nepřipojeno" —
 * dřívější ruční volba Náhled/Zkušební mizí, formulář při uložení vždy
 * zapíše mode `TEST` (viz `ConnectionWizard`).
 */
function humanConnectionStatus(state: PortalConnectionState): 'disconnected' | 'connected' | 'error' {
  if (state === 'connected') return 'connected';
  if (state === 'error') return 'error';
  return 'disconnected';
}

/**
 * Krok PORT4 — CO KANCELAR POTREBUJE A ODPOVIDA VUBEC ROZHRANI PORTALU.
 *
 * Vse prichazi z `/v1/connectors/portal-access` (profil rodiny SR-RPC +
 * zapsany vysledek `pnpm portals:probe`): zdroj pristupu je KOD, veta k nemu
 * i stav rozhrani jsou v i18n. Datum mereni je u stavu vzdy — stary vysledek
 * se nesmi cist jako dnesek.
 */
function PortalAccessBlock({
  access,
  name,
}: {
  readonly access: PortalAccessDto;
  /** Nazev portalu z registru — do vety „<portal> přijímá jen pronájmy". */
  readonly name: string;
}): React.JSX.Element {
  const where = access.where;
  /**
   * Krok PORT12: nova pole DTO snasi i CHYBEJICI hodnotu — starsi nasazene API
   * je neposila a obrazovka se kvuli tomu nesmi rozbit.
   */
  const group = access.group ?? null;
  const welcomeCredit = access.welcomeCredit ?? null;
  const officeFees = access.officeFees ?? null;
  return (
    <div className="text-sm mt-2" data-testid={`portal-access-${access.portal}`}>
      <p>
        <span className="font-medium">{t('webAdmin.portaly.officeAccessLabel', { locale: 'cs' })}:</span>{' '}
        {t(`webAdmin.portaly.officeAccessSource.${access.source}` as MessageKey, { locale: 'cs' })}
      </p>
      {where !== null ? (
        <p>
          {t('webAdmin.portaly.officeAccessWhere', { locale: 'cs' })}:{' '}
          {where.startsWith('http') ? (
            <a
              className="rh-a2-link"
              href={where}
              target="_blank"
              rel="noreferrer"
              data-testid={`portal-access-link-${access.portal}`}
            >
              {where}
            </a>
          ) : (
            <a className="rh-a2-link" href={`mailto:${where}`} data-testid={`portal-access-link-${access.portal}`}>
              {where}
            </a>
          )}
        </p>
      ) : null}
      <p style={{ color: 'var(--rh-text-muted)' }} data-testid={`portal-interface-${access.portal}`}>
        {t(`webAdmin.portaly.interfaceState.${access.interfaceState}` as MessageKey, { locale: 'cs' })}
        {access.interfaceVersion !== null
          ? ` (${t('webAdmin.portaly.interfaceVersion', {
              locale: 'cs',
              values: { version: access.interfaceVersion },
            })})`
          : ''}
        {' · '}
        {t('webAdmin.portaly.interfaceCheckedAt', {
          locale: 'cs',
          values: { checkedAt: access.interfaceCheckedAt },
        })}
      </p>
      {access.insecureTransport ? (
        <p className="rh-badge rh-badge--warning mt-1" data-testid={`portal-insecure-${access.portal}`}>
          {t('webAdmin.portaly.insecureTransportWarning', { locale: 'cs' })}
        </p>
      ) : null}
      {/* Krok PORT10: portal, ktery inzeruje jen pronajmy (DomuHQ, §6.1 jeho
          dokumentace). Kancelar to ma vedet driv, nez na nem bude cekat
          prodej — odeslani zastavi uz validator a driver. */}
      {access.rentalsOnly ? (
        <p className="rh-badge rh-badge--warning mt-1" data-testid={`portal-rentals-only-${access.portal}`}>
          {t('webAdmin.portaly.rentalsOnlyWarning', { locale: 'cs', values: { portal: name } })}
        </p>
      ) : null}
      {/* Krok PORT12: jedno pripojeni = vic webu (B3 Technology: sest). Vycet
          i jmeno provozovatele jdou z profilu portalu pres API — obrazovka si
          je neopisuje. */}
      {group !== null ? (
        <p style={{ color: 'var(--rh-text-muted)' }} data-testid={`portal-group-${access.portal}`}>
          {t('webAdmin.portaly.portalGroup', {
            locale: 'cs',
            values: {
              count: String(group.portals.length),
              operator: group.operator,
              portals: group.portals.join(', '),
            },
          })}
        </p>
      ) : null}
      {/* Krok PORT12: uvitaci kredit pro nove kancelare pres Makleřova. */}
      {welcomeCredit !== null ? (
        <p style={{ color: 'var(--rh-text-muted)' }} data-testid={`portal-welcome-credit-${access.portal}`}>
          {t('webAdmin.portaly.welcomeCredit', {
            locale: 'cs',
            values: { amount: formatMoney(welcomeCredit) ?? '' },
          })}
        </p>
      ) : null}
      {/* Krok PORT14: co portal uctuje KANCELARI (Bazoš: 2 Kc za vlozeni,
          49 Kc za topovani). Castky jdou z profilu portalu pres API jako
          Money — obrazovka si je neopisuje a formatuje je tim, co formatuje
          ceny nemovitosti. */}
      {officeFees !== null ? (
        <p style={{ color: 'var(--rh-text-muted)' }} data-testid={`portal-office-fees-${access.portal}`}>
          {t('webAdmin.portaly.officeFees', {
            locale: 'cs',
            values: {
              insert: formatMoney(officeFees.insert) ?? '',
              top: formatMoney(officeFees.top) ?? '',
            },
          })}
        </p>
      ) : null}
      {/* Krok PORT11: portal s kratsim limitem popisu nez Sreality (ToReality
          3000 znaku v „prisnem Sreality rezimu"). Cislo jde z profilu portalu
          pres API — UI si ho neopisuje; pocitadlo znaku v konceptu uz ten
          nejkratsi limit drzi (`text.maxLength`). */}
      {access.descriptionMaxChars !== null ? (
        <p style={{ color: 'var(--rh-text-muted)' }} data-testid={`portal-description-limit-${access.portal}`}>
          {t('webAdmin.portaly.descriptionLimit', {
            locale: 'cs',
            values: { chars: String(access.descriptionMaxChars) },
          })}
        </p>
      ) : null}
    </div>
  );
}

/**
 * Krok PORT8 — ODKAZ NA FEED PRO UNITED CLASSIFIEDS (Nehnuteľnosti.sk,
 * Reality.sk, TopReality.sk).
 *
 * Portal si inzerci STAHUJE SAM kazdych 12 hodin, takze kancelar nic nikam
 * neodesila — jen jednou nahlasi URL feedu podpore UC (a predtim si u
 * obchodniho zastupce objedna externi import). Token je videt JEDINE ve
 * chvili vygenerovani: v DB lezi jen jeho hash, takze odkaz uz nikdo
 * (ani my) znovu neprecte a „Zkopírovat URL feedu" ma smysl hned tady.
 */
function UnitedClassifiedsFeedBlock(): React.JSX.Element {
  const statusQuery = useQuery({
    queryKey: ['uc-feed-status'],
    queryFn: fetchUnitedClassifiedsFeedStatus,
  });
  const [url, setUrl] = React.useState<string | null>(null);
  const [copied, setCopied] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const rotate = useMutation({
    mutationFn: rotateUnitedClassifiedsFeedToken,
    onSuccess: (token) => {
      setError(null);
      setCopied(false);
      setUrl(unitedClassifiedsFeedUrl(token));
      void statusQuery.refetch();
    },
    onError: (err: unknown) => setError(err instanceof Error ? err.message : String(err)),
  });

  async function copy(value: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
    } catch {
      // Bez `navigator.clipboard` se nic nepredstira — URL je vypsana vedle.
      setCopied(false);
    }
  }

  return (
    <div className="text-sm mt-2" data-testid="uc-feed-block">
      <p>{t('webAdmin.portaly.unitedClassifieds.whatOfficeMustDo', { locale: 'cs' })}</p>
      <p style={{ color: 'var(--rh-text-muted)' }} data-testid="uc-feed-status">
        {statusQuery.data?.active === true
          ? t('webAdmin.portaly.unitedClassifieds.linkExists', { locale: 'cs' })
          : t('webAdmin.portaly.unitedClassifieds.linkMissing', { locale: 'cs' })}
      </p>
      <button
        type="button"
        className="px-4 py-1 mt-1 bg-neutral-900 text-rh-on-accent rounded text-sm"
        data-testid="uc-feed-generate"
        disabled={rotate.isPending}
        onClick={() => rotate.mutate()}
      >
        {statusQuery.data?.active === true
          ? t('webAdmin.portaly.unitedClassifieds.regenerateLink', { locale: 'cs' })
          : t('webAdmin.portaly.unitedClassifieds.generateLink', { locale: 'cs' })}
      </button>
      {url !== null ? (
        <div className="mt-2">
          <p className="break-all" data-testid="uc-feed-url">
            {url}
          </p>
          <button
            type="button"
            className="rh-a2-link mt-1"
            data-testid="uc-feed-copy"
            onClick={() => void copy(url)}
          >
            {t('webAdmin.portaly.unitedClassifieds.copyFeedUrl', { locale: 'cs' })}
          </button>
          {copied ? (
            <span className="ml-2" style={{ color: 'var(--rh-text-muted)' }} data-testid="uc-feed-copied">
              {t('webAdmin.portaly.unitedClassifieds.copied', { locale: 'cs' })}
            </span>
          ) : null}
          <p className="mt-1" style={{ color: 'var(--rh-text-muted)' }}>
            {t('webAdmin.portaly.unitedClassifieds.linkShownOnce', { locale: 'cs' })}
          </p>
        </div>
      ) : null}
      {error !== null ? (
        <p className="rh-badge rh-badge--danger mt-1" data-testid="uc-feed-error">
          {error}
        </p>
      ) : null}
    </div>
  );
}

function ConnectionWizard({
  descriptor,
  connection,
  access,
  onChanged,
}: {
  readonly descriptor: PortalDescriptor;
  readonly connection: ConnectionDto | undefined;
  /** Krok PORT4: `undefined` u portalu mimo rodinu SR-RPC (nic se nekresli). */
  readonly access: PortalAccessDto | undefined;
  readonly onChanged: () => void;
}): React.JSX.Element {
  const [fields, setFields] = React.useState<Record<string, string>>({});
  const [formOpen, setFormOpen] = React.useState(false);
  const [message, setMessage] = React.useState<string | null>(null);

  const connect = useMutation({
    mutationFn: async () => {
      await saveConnection(descriptor.portal, { mode: 'TEST', enabled: true });
      if (Object.keys(fields).length > 0) {
        await saveCredentials(descriptor.portal, fields);
      }
    },
    onSuccess: () => {
      setMessage(null);
      setFields({});
      setFormOpen(false);
      onChanged();
    },
    onError: (err: unknown) => setMessage(err instanceof Error ? err.message : String(err)),
  });

  // Krok SR1: napojitelny je portal, pro ktery mame konektor — i kdyz jeho
  // kompatibilitu zatim jen tvrdi provozovatel (rodina SR-RPC). Bez konektoru
  // (PENDING_SPEC, adresar trhu) se pristupy vyplnit nedaji.
  const notConnectable = !descriptor.connectorImplemented;

  const state = connectionState(descriptor, connection);
  const humanState = humanConnectionStatus(state);
  const hasFields = descriptor.auth.tenantCredentialFields.length > 0;

  return (
    <div className="rh-card p-4" data-testid={`portal-card-${descriptor.portal}`}>
      <p className="flex items-center gap-2 flex-wrap">
        <span className="font-semibold">{descriptor.name}</span>
        {notConnectable ? (
          <span className={badgeClass(descriptor.status === 'DOCS_VERIFIED' ? 'NOT_PUBLISHED' : descriptor.status)}>
            {PORTAL_STATUS_LABELS[descriptor.status] ?? descriptor.status}
          </span>
        ) : (
          <span className={connectionBadgeClass[state]} data-testid={`portal-state-${descriptor.portal}`}>
            {t(`webAdmin.portaly.connectionStatus.${humanState}`, { locale: 'cs' })}
          </span>
        )}
      </p>

      {access !== undefined ? <PortalAccessBlock access={access} name={descriptor.name} /> : null}
      {descriptor.portal === UNITED_CLASSIFIEDS_PORTAL ? <UnitedClassifiedsFeedBlock /> : null}

      {notConnectable ? (
        <>
          <p className="text-sm mt-2" data-testid="not-connectable">
            <span className="font-medium">{t('webAdmin.portaly.readyToConnect', { locale: 'cs' })}</span>{' '}
            {descriptor.status === 'PENDING_SPEC'
              ? t('webAdmin.portaly.missingSpec', { locale: 'cs' })
              : t('webAdmin.portaly.missingPartnerActivation', { locale: 'cs' })}
          </p>
          <p className="text-sm mt-1" style={{ color: 'var(--rh-text-muted)' }}>
            {descriptor.docVersion !== null
              ? t('webAdmin.portaly.docVersionLine', {
                  locale: 'cs',
                  values: { docVersion: descriptor.docVersion, checkedAt: descriptor.docsCheckedAt ?? '—' },
                })
              : t('webAdmin.portaly.docsNotPublic', { locale: 'cs' })}
          </p>
        </>
      ) : (
        <div className="mt-2">
          {humanState === 'connected' ? (
            <p className="text-sm" style={{ color: 'var(--rh-text-muted)' }}>
              {connection?.secretHint !== null && connection?.secretHint !== undefined
                ? t('webAdmin.portaly.savedPrefix', { locale: 'cs', values: { hint: connection.secretHint } })
                : null}{' '}
              <button
                type="button"
                className="rh-a2-link"
                data-testid={`portal-connect-${descriptor.portal}`}
                onClick={() => setFormOpen(true)}
              >
                {t('webAdmin.portaly.changeCredentials', { locale: 'cs' })}
              </button>
            </p>
          ) : !formOpen ? (
            <button
              type="button"
              className="px-4 py-1 bg-neutral-900 text-rh-on-accent rounded text-sm"
              data-testid={`portal-connect-${descriptor.portal}`}
              onClick={() => (hasFields ? setFormOpen(true) : connect.mutate())}
            >
              {humanState === 'error'
                ? t('webAdmin.portaly.fixCredentials', { locale: 'cs' })
                : t('webAdmin.portaly.connectAccount', { locale: 'cs' })}
            </button>
          ) : null}

          {formOpen && hasFields ? (
            <form
              className="mt-2 flex flex-wrap items-end gap-2"
              data-testid={`portal-credentials-form-${descriptor.portal}`}
              onSubmit={(event) => {
                event.preventDefault();
                connect.mutate();
              }}
            >
              <p className="text-sm basis-full" style={{ color: 'var(--rh-text-muted)' }}>
                {tryT(`webAdmin.portaly.credentialHint.${descriptor.portal}` as MessageKey, { locale: 'cs' }) ??
                  t('webAdmin.portaly.credentialHintGeneric', { locale: 'cs' })}
              </p>
              {descriptor.auth.tenantCredentialFields.map((field) => (
                <label key={field.key} className="text-sm">
                  {tryT(`webAdmin.portaly.credentialField.${field.key}` as MessageKey, { locale: 'cs' }) ?? field.key}
                  <input
                    type={field.kind === 'secret' ? 'password' : 'text'}
                    autoComplete="off"
                    className="border rounded px-2 py-1 text-sm block"
                    value={fields[field.key] ?? ''}
                    onChange={(event) =>
                      setFields((prev) => ({ ...prev, [field.key]: event.target.value }))
                    }
                  />
                </label>
              ))}
              <button
                type="submit"
                className="px-4 py-1 bg-neutral-900 text-rh-on-accent rounded text-sm"
                disabled={Object.keys(fields).length === 0}
              >
                {t('webAdmin.portaly.saveAndConnect', { locale: 'cs' })}
              </button>
              <button type="button" className="px-4 py-1 border rounded text-sm" onClick={() => setFormOpen(false)}>
                {t('webAdmin.portaly.cancelConnect', { locale: 'cs' })}
              </button>
            </form>
          ) : null}
        </div>
      )}
      {message !== null ? (
        <p className="text-sm mt-2" data-testid="wizard-message">
          {message}
        </p>
      ) : null}
    </div>
  );
}

// ---- krok UXL1 bod 2: dve zalozky (Publikace / Pripojeni) -----------------

const PORTAL_TABS = [
  { key: 'publikace', labelKey: 'webAdmin.portaly.tabPublications' },
  { key: 'pripojeni', labelKey: 'webAdmin.portaly.tabConnections' },
] as const;
type PortalTab = (typeof PORTAL_TABS)[number]['key'];

/**
 * Krok UXL1 bod 2 — matice je stale pres `ListTable` (FUNK1 bod 2 pro 390px),
 * ale nad ni pribylo hledani podle nemovitosti a filtr stavu. Prehledovy
 * dashboard nad VSEMI portaly najednou by byl treti rozpracovany pohled —
 * zustava proto na matici, jen filtrovane.
 */
function PublicationsTab({
  view,
  connectable,
  cellMap,
  selected,
  onSelect,
  onChanged,
  onRequestAccess,
}: {
  readonly view: SyncCenterView;
  readonly connectable: readonly PortalDescriptor[];
  readonly cellMap: ReadonlyMap<string, SyncCenterCell>;
  readonly selected: SyncCenterCell | null;
  readonly onSelect: (cell: SyncCenterCell) => void;
  readonly onChanged: () => void;
  /** Krok UXF2 bod 7: „Požádat o přístup" otevira EXISTUJICI podporu. */
  readonly onRequestAccess: () => void;
}): React.JSX.Element {
  const [search, setSearch] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState('');
  const [now] = React.useState(() => systemClock.now());

  /** Tytez vety jako „Poslední změna" v Zakazkach (`webAdmin.relativeTime.*`). */
  const relativeTexts = {
    today: (time: string) => t('webAdmin.relativeTime.today', { locale: 'cs', values: { time } }),
    yesterday: (time: string) => t('webAdmin.relativeTime.yesterday', { locale: 'cs', values: { time } }),
  };

  /** Veta „dalsiho kroku" z katalogu; vyber vety dela `cellNextStep`. */
  const nextStepSentence = (status: string): string =>
    tryT(`webAdmin.portaly.nextStep.${status}` as MessageKey, { locale: 'cs' }) ?? '';

  const statusOptions = React.useMemo(() => {
    const seen = new Set<string>();
    for (const cell of cellMap.values()) seen.add(cell.status);
    return [...seen].sort();
  }, [cellMap]);

  const filteredListings = React.useMemo(() => {
    const query = search.trim().toLowerCase();
    return view.listings.filter((listing) => {
      if (
        query !== '' &&
        !listing.address.toLowerCase().includes(query) &&
        !listing.internalCode.toLowerCase().includes(query)
      )
        return false;
      if (statusFilter === '') return true;
      return connectable.some((portal) => cellMap.get(`${listing.listingId}:${portal.portal}`)?.status === statusFilter);
    });
  }, [view.listings, search, statusFilter, connectable, cellMap]);

  return (
    <div data-testid="portaly-tab-panel-publikace">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <input
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t('webAdmin.portaly.searchPlaceholder', { locale: 'cs' })}
          className="border rounded px-2 py-1 text-sm"
          data-testid="portaly-search"
          aria-label={t('webAdmin.portaly.searchPlaceholder', { locale: 'cs' })}
        />
        <select
          value={statusFilter}
          onChange={(event) => setStatusFilter(event.target.value)}
          className="border rounded px-2 py-1 text-sm"
          data-testid="portaly-status-filter"
          aria-label={t('webAdmin.portaly.statusFilterLabel', { locale: 'cs' })}
        >
          <option value="">{t('webAdmin.portaly.statusFilterAll', { locale: 'cs' })}</option>
          {statusOptions.map((status) => (
            <option key={status} value={status}>
              {CELL_STATUS_LABELS[status] ?? status}
            </option>
          ))}
        </select>
      </div>

      {/*
        Krok UXF2 bod 7 (nalez N6) — VYCHOZI RADEK JE NEMOVITOST A SOUHRN.
        Matice nemovitost x 7 portalu znamenala na 390 px sedm dlouhych radek
        na kazdou nabidku, z toho vetsina „Nepřipojeno". Radek proto nese
        souhrn „N z 7 zveřejněno" a stavy jednotlivych portalu jsou v
        rozbaleni (`<details>`) — tytez prvky, tytez `data-testid`, jen
        schovane. Na 390 px z radku porad dela kartu `ListTable` (FUNK1 bod 2).
      */}
      <ListTable
        testId="sync-matrix"
        caption={t('webAdmin.portaly.pageTitle', { locale: 'cs' })}
        rows={filteredListings}
        rowKey={(listing) => listing.listingId}
        /*
          Krok UXD1b — JEDNOTNY PRAZDNY STAV. Po zuzeni hledanim/filtrem
          zustala prazdna tabulka bez jedine vety (jen zahlavi sloupcu) —
          tytez `EmptyState`, jaky uz ma chyba nacteni teze obrazovky a
          seznam ohlasu v Poptavkach.
        */
        empty={
          <span data-testid="sync-matrix-empty">
            <EmptyState
              title={t('webAdmin.portaly.emptyFilteredTitle', { locale: 'cs' })}
              description={t('webAdmin.portaly.emptyFilteredDescription', { locale: 'cs' })}
            />
          </span>
        }
        columns={[
          {
            key: 'property',
            label: t('webAdmin.portaly.propertyColumn', { locale: 'cs' }),
            cell: (listing) => (
              <>
                <span className="font-medium">{listing.address}</span>{' '}
                {muted(CATEGORY_LABELS[listing.category] ?? listing.category)}{' '}
                {/* Krok LIST1 bod 4: stav inzeratu — tytez popisky jako blok
                    na detailu zakazky (`listingDealStatusLabel`). */}
                <span data-testid={`sync-deal-status-${listing.listingId}`}>
                  {muted(listingDealStatusLabel(syncCenterDealStatus(listing)))}
                </span>
              </>
            ),
          },
          {
            key: 'portals',
            label: t('webAdmin.portaly.portalsColumn', { locale: 'cs' }),
            cell: (listing) => {
              /*
               * Krok UXP4b bod 3, oprava 1 (nález P2-8): jmenovatel je počet
               * portálů, KAM JDE ZVEŘEJNIT — tentýž, který nabízí Nový
               * inzerát. Portály „Vyžaduje partnera" zůstávají v rozbalení
               * (i s „Požádat o přístup"), jen se nepočítají mezi možnosti,
               * které makléř dnes má. Krok SEZ1: oba počty počítá
               * `portalExportSummary`, aby seznam nemovitostí a Portály
               * netvrdily dvě různá čísla.
               */
              const summary = portalExportSummary({ portals: connectable, cellMap, listingId: listing.listingId });
              const published = summary.published;
              const publishable = summary.total;
              const cells = summary.rows.map((row) => ({ portal: row.descriptor, cell: row.cell }));
              return (
                <details data-testid={`sync-portals-${listing.listingId}`}>
                  <summary data-testid={`sync-summary-${listing.listingId}`}>
                    {t('webAdmin.portaly.publishedSummary', {
                      locale: 'cs',
                      values: { published, total: publishable },
                    })}
                  </summary>
                  <ul className="rh-a2-facts">
                    {cells.map(({ portal, cell }) => (
                      <li key={portal.portal}>
                        <span className="rh-a2-k">{portal.name}</span>
                        <span className="rh-a2-v">
                          {cell === undefined ? (
                            ''
                          ) : (
                            <>
                              <button
                                type="button"
                                className={badgeClass(cell.status)}
                                data-testid={`cell-${listing.listingId}-${portal.portal}`}
                                onClick={() => onSelect(cell)}
                                title={cell.lastErrorCode ?? undefined}
                              >
                                {CELL_STATUS_LABELS[cell.status] ?? cell.status}
                              </button>{' '}
                              {cell.status === 'PARTNER_REQUIRED' ? (
                                <button
                                  type="button"
                                  className="rh-a2-link"
                                  onClick={() => onRequestAccess()}
                                  data-testid={`sync-request-access-${listing.listingId}-${portal.portal}`}
                                >
                                  {t('webAdmin.portaly.requestAccess', { locale: 'cs' })}
                                </button>
                              ) : null}
                              {/*
                                Krok UXD1b — PLOCHY RADEK: portal · stav ·
                                POSLEDNI ZMENA · DALSI KROK (ZBYVA z UXL1).
                                Do UXD1b nesl radek jen odznak stavu, takze
                                makler nevedel, jak je stav stary ani co s nim.
                                Cas kresli `formatRelativeChange` — tataz
                                funkce jako sloupec „Poslední změna" v
                                Zakazkach a Klientech; dalsi krok rozhoduje
                                `cellNextStep` (SSOT v `lib/connectors.ts`).
                              */}
                              <span
                                className="rh-a2-meta rh-a2-nowrap"
                                data-testid={`sync-last-change-${listing.listingId}-${portal.portal}`}
                              >
                                {' · '}
                                {cell.lastChangeAt === null || cell.lastChangeAt === undefined
                                  ? t('webAdmin.portaly.lastChangeNever', { locale: 'cs' })
                                  : formatRelativeChange(cell.lastChangeAt, now, relativeTexts)}
                              </span>
                              <span
                                className="rh-a2-meta"
                                data-testid={`sync-next-step-${listing.listingId}-${portal.portal}`}
                              >
                                {' · '}
                                {cellNextStep(cell, nextStepSentence)}
                              </span>
                            </>
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                </details>
              );
            },
          },
        ]}
      />

      {selected !== null ? (
        <PublicationPanel
          cell={cellMap.get(`${selected.listingId}:${selected.portal}`) ?? selected}
          onChanged={onChanged}
        />
      ) : null}
    </div>
  );
}

// ---- stranka ---------------------------------------------------------------

export function PortalyPage(): React.JSX.Element {
  const queryClient = useQueryClient();
  const registry = useQuery({ queryKey: ['connector-registry'], queryFn: fetchRegistry });
  // Krok PORT4: „co kancelar potrebuje" + stav rozhrani (snimek probe).
  const portalAccess = useQuery({ queryKey: ['connector-portal-access'], queryFn: fetchPortalAccess });
  const connections = useQuery({ queryKey: ['connector-connections'], queryFn: fetchConnections });
  const syncCenter = useQuery({ queryKey: ['sync-center'], queryFn: fetchSyncCenter });
  const [selected, setSelected] = React.useState<SyncCenterCell | null>(null);
  const [activeTab, setActiveTab] = React.useState<PortalTab>('publikace');
  /**
   * Krok UXF2 bod 7: „Vyžaduje partnera" uz neni jen stav — makler ma u nej
   * akci „Požádat o přístup". Zada se EXISTUJICI podporou (`SupportWidget`
   * v ovladanem rezimu, tentyz vzor jako paticka panelu v `layout.tsx`),
   * zadna nova cesta ani nove API.
   */
  const [supportOpen, setSupportOpen] = React.useState(false);

  const refreshAll = React.useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['sync-center'] });
    void queryClient.invalidateQueries({ queryKey: ['connector-connections'] });
  }, [queryClient]);

  if (registry.isError || syncCenter.isError) {
    return (
      <EmptyState
        title={t('webAdmin.portaly.loadErrorTitle', { locale: 'cs' })}
        description={String((registry.error ?? syncCenter.error) ?? '')}
      />
    );
  }
  const portals = registry.data ?? [];
  // Krok SR1: registr ma pul stovky portalu. Matice i karty pristupu patri
  // jen tem, na ktere umime odeslat; zbytek trhu je mimo kancelar (krok
  // UXP6) — jinak by tabulka mela 58 sloupcu.
  const connectable = portals.filter((portal) => portal.connectorImplemented);
  const view = syncCenter.data;
  const cellMap = new Map<string, SyncCenterCell>(
    (view?.cells ?? []).map((cell) => [`${cell.listingId}:${cell.portal}`, cell]),
  );
  const connectionMap = new Map<string, ConnectionDto>(
    (connections.data ?? []).map((connection) => [connection.portal, connection]),
  );
  const accessMap = new Map<string, PortalAccessDto>(
    (portalAccess.data ?? []).map((access) => [access.portal, access]),
  );
  /**
   * Krok PORT4: na zalozku Pripojeni patri i portaly rodiny SR-RPC, kam se
   * zatim odeslat neda (chybi adresa nebo jde jen po HTTP). Prave u nich
   * potrebuje kancelar vedet, CO ma zaridit — bez nich by obrazovka o sedmi
   * portalech z pruzkumu mlcela.
   */
  const withAccessGuidance = portals.filter(
    (portal) => portal.connectorImplemented || accessMap.has(portal.portal),
  );

  return (
    <div className="p-4" data-testid="portaly-page">
      <PageHead
        title={t('webAdmin.portaly.pageTitle', { locale: 'cs' })}
        subtitle={t('webAdmin.portaly.pageDescription', { locale: 'cs' })}
      />

      {/* Krok UXF4 bod 5: tatáz rolovatelna lista jako v Nastaveni — druhy
          pruh zalozek s `overflow-hidden` by se rozesel prvni zmenou. */}
      <div className="rh-a2-tabs mb-4 min-w-0" role="tablist" data-testid="portaly-tabs">
        {PORTAL_TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.key}
            className={activeTab === tab.key ? 'is-on' : ''}
            data-testid={`portaly-tab-${tab.key}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {t(tab.labelKey, { locale: 'cs' })}
          </button>
        ))}
      </div>

      {view === undefined ? (
        muted(t('webAdmin.portaly.loading', { locale: 'cs' }))
      ) : activeTab === 'publikace' ? (
        <PublicationsTab
          view={view}
          connectable={connectable}
          cellMap={cellMap}
          selected={selected}
          onSelect={setSelected}
          onChanged={refreshAll}
          onRequestAccess={() => setSupportOpen(true)}
        />
      ) : (
        <div data-testid="portaly-tab-panel-pripojeni">
          <h2 className="text-lg font-semibold mb-2">{t('webAdmin.portaly.settingsHeading', { locale: 'cs' })}</h2>
          <div className="grid gap-3">
            {withAccessGuidance.map((portal) => (
              <ConnectionWizard
                key={portal.portal}
                descriptor={portal}
                connection={connectionMap.get(portal.portal)}
                access={accessMap.get(portal.portal)}
                onChanged={refreshAll}
              />
            ))}
          </div>

          <LiveExportSection />
          <OwnerApprovalSection />
          <ExportPolicySection />
          <PortalDirectoryNotice />
        </div>
      )}

      {/* Panel podpory pro „Požádat o přístup" (krok UXF2 bod 7). */}
      <SupportWidget open={supportOpen} onOpenChange={setSupportOpen} />
    </div>
  );
}

/**
 * Krok P1EXP1 bod 1 — OSTRY EXPORT KANCELARE.
 *
 * Prepinac (`tenant_settings.portal_live_export`) je GO pro rezim LIVE: bez
 * nej pripojeni v rezimu LIVE nejde ani zalozit a uz zarazena operace se
 * neodesle. Pristupy k portalu tenhle prepinac NEDAVA — ty se zadavaji
 * u portalu vys na teto obrazovce (sifrovane, nikdy v kodu).
 */
function LiveExportSection(): React.JSX.Element {
  const queryClient = useQueryClient();
  const settings = useQuery({ queryKey: ['tenant-settings'], queryFn: getTenantSettings });
  const save = useMutation({
    mutationFn: (next: boolean) => setPortalLiveExport(next),
    onSuccess: (updated) => queryClient.setQueryData(['tenant-settings'], updated),
  });
  const enabled = settings.data?.portalLiveExport ?? false;

  return (
    <section className="rh-card p-4 mt-4" data-testid="portaly-live-export">
      <h2 className="text-lg font-semibold mb-1">{t('webAdmin.portaly.liveExport.heading', { locale: 'cs' })}</h2>
      <p className="text-sm mb-2" style={{ color: 'var(--rh-text-muted)' }}>
        {t('webAdmin.portaly.liveExport.hint', { locale: 'cs' })}
      </p>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={enabled}
          disabled={settings.data === undefined || save.isPending}
          onChange={(event) => { save.mutate(event.target.checked); }}
          data-testid="portaly-live-export-toggle"
        />
        <span data-testid="portaly-live-export-state">
          {t(enabled ? 'webAdmin.portaly.liveExport.on' : 'webAdmin.portaly.liveExport.off', { locale: 'cs' })}
        </span>
      </label>
      {save.isError ? (
        <p role="alert" className="text-sm mt-2" style={{ color: 'var(--rh-danger)' }}>
          {t('webAdmin.portaly.liveExport.saveFailed', { locale: 'cs' })}
        </p>
      ) : null}
    </section>
  );
}

/**
 * Krok SCHV1 bod 4 — VYZADOVAT SCHVALENI MAJITELEM PRED EXPORTEM.
 *
 * Vychozi VYPNUTO (zadani): makler dostane u zakazky jen upozorneni
 * „majitel zatim neschvalil" a muze ho vedome prekrocit. Zapnuto = operace
 * na externi portal se bez souhlasu nezaradi (brana je na serveru,
 * `@reos/listing`; tady je jen prepinac kancelare). Web kancelare jde
 * zverejnit kdykoli — ten se zapina na karte zakazky, ne publikaci.
 */
function OwnerApprovalSection(): React.JSX.Element {
  const queryClient = useQueryClient();
  const settings = useQuery({ queryKey: ['tenant-settings'], queryFn: getTenantSettings });
  const save = useMutation({
    mutationFn: (next: boolean) => setRequireOwnerApproval(next),
    onSuccess: (updated) => queryClient.setQueryData(['tenant-settings'], updated),
  });
  const enabled = settings.data?.requireOwnerApproval ?? false;

  return (
    <section className="rh-card p-4 mt-4" data-testid="portaly-owner-approval">
      <h2 className="text-lg font-semibold mb-1">{t('webAdmin.portaly.ownerApproval.heading', { locale: 'cs' })}</h2>
      <p className="text-sm mb-2" style={{ color: 'var(--rh-text-muted)' }}>
        {t('webAdmin.portaly.ownerApproval.hint', { locale: 'cs' })}
      </p>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={enabled}
          disabled={settings.data === undefined || save.isPending}
          onChange={(event) => { save.mutate(event.target.checked); }}
          data-testid="portaly-owner-approval-toggle"
        />
        <span>{t('webAdmin.portaly.ownerApproval.label', { locale: 'cs' })}</span>
      </label>
      {save.isError ? (
        <p role="alert" className="text-sm mt-2" style={{ color: 'var(--rh-danger)' }}>
          {t('webAdmin.portaly.liveExport.saveFailed', { locale: 'cs' })}
        </p>
      ) : null}
    </section>
  );
}

// ---- krok SR1 bod 5: makleri, kteri exportuji jen po schvaleni -------------

function ExportPolicySection(): React.JSX.Element {
  const queryClient = useQueryClient();
  const members = useQuery({ queryKey: ['members'], queryFn: listMembers });
  const policy = useQuery({ queryKey: ['export-policy'], queryFn: fetchExportPolicy });
  const save = useMutation({
    mutationFn: (input: { userId: string; novice: boolean }) =>
      saveExportPolicy(input.userId, input.novice),
    onSuccess: (novices) => queryClient.setQueryData(['export-policy'], novices),
  });
  const novices = new Set(policy.data ?? []);

  return (
    <section className="mt-6" data-testid="export-policy">
      <h2 className="text-lg font-semibold mb-1">{t('webAdmin.portaly.sr1.noveHeading', { locale: 'cs' })}</h2>
      <p className="text-sm mb-2" style={{ color: 'var(--rh-text-muted)' }}>
        {t('webAdmin.portaly.sr1.noveLede', { locale: 'cs' })}
      </p>
      <div className="grid gap-1">
        {(members.data ?? []).map((member) => (
          <label key={member.userId} className="text-sm flex items-center gap-2">
            <input
              type="checkbox"
              checked={novices.has(member.userId)}
              onChange={(event) => save.mutate({ userId: member.userId, novice: event.target.checked })}
              data-testid={`export-novice-${member.userId}`}
            />
            <span>{member.name}</span>
            <span className="rh-badge rh-badge--neutral">
              {novices.has(member.userId)
                ? t('webAdmin.portaly.sr1.noviceOn', { locale: 'cs' })
                : t('webAdmin.portaly.sr1.noviceOff', { locale: 'cs' })}
            </span>
          </label>
        ))}
      </div>
    </section>
  );
}

// ---- krok UXP6: zbytek trhu bez provozniho adresare -----------------------

/**
 * Krok UXP6 bod 1: puvodni adresar (stav nasi zadosti o napojeni, kontakty
 * na portaly, poznamky z pruzkumu trhu) byl provozni info RedHand, ne
 * kancelare — misto tabulky zustava jen jedna veta. Adresar samotny (pro
 * platformovou roli RedHand) jde pres `listPortalDescriptors()` primo,
 * mimo tenant HTTP API (viz `/v1/connectors/registry` v listing pluginu).
 */
function PortalDirectoryNotice(): React.JSX.Element {
  return (
    <p className="text-sm mt-6" style={{ color: 'var(--rh-text-muted)' }} data-testid="portal-directory-notice">
      {t('webAdmin.portaly.sr1.directoryNotice', { locale: 'cs' })}
    </p>
  );
}
